export enum CommercialStage {
  INTEREST = 'interest',
  FIRST_CONTACT = 'first_contact',
  FUP = 'fup',
  DOUBT = 'doubt',
  NEGOTIATION = 'negotiation',
  SCHEDULING = 'scheduling',
  PAYMENT = 'payment',
  DECLINED = 'declined',
  CONVERTED = 'converted',
  /** @deprecated Use CONVERTED */
  SALE = 'sale',
}

export const COMMERCIAL_STAGE_OPTIONS = [
  {
    value: CommercialStage.INTEREST,
    label: 'Interesse',
    description: 'Lead recém-chegada no funil comercial',
  },
  {
    value: CommercialStage.FIRST_CONTACT,
    label: '1º contato',
    description: 'Contato de alguém do comercial',
  },
  {
    value: CommercialStage.FUP,
    label: 'Fup',
    description: 'Sem resposta ao 1º contato; enviamos até 2 fups',
  },
  {
    value: CommercialStage.DOUBT,
    label: 'Dúvidas',
    description: 'Explicamos produto, entrega e metodologia da Mari',
  },
  {
    value: CommercialStage.NEGOTIATION,
    label: 'Negociação',
    description: 'Enviamos os valores e estamos negociando o fechamento',
  },
  {
    value: CommercialStage.SCHEDULING,
    label: 'Agendamento',
    description: 'Aluna em processo de agendamento',
  },
  {
    value: CommercialStage.PAYMENT,
    label: 'Pagamento',
    description: 'Aguardando ou processando pagamento',
  },
  {
    value: CommercialStage.DECLINED,
    label: 'Declinado',
    description: 'Mensagem encerrando o contato e objeção do porquê não fechou',
  },
  {
    value: CommercialStage.CONVERTED,
    label: 'Venda',
    description: 'Lead convertida em venda',
  },
] as const;

export const COMMERCIAL_STAGE_UNSET_FILTER_VALUE = '__unset__';

export const COMMERCIAL_STAGE_FILTER_OPTIONS = [
  { value: COMMERCIAL_STAGE_UNSET_FILTER_VALUE, label: 'Sem estágio' },
  ...COMMERCIAL_STAGE_OPTIONS.map(({ value, label }) => ({ value, label })),
];

const COMMERCIAL_STAGE_LABELS = Object.fromEntries(
  COMMERCIAL_STAGE_OPTIONS.map((option) => [option.value, option.label]),
) as Record<CommercialStage, string>;

export const COMMERCIAL_STAGE_VALUES = new Set<string>([
  ...COMMERCIAL_STAGE_OPTIONS.map((option) => option.value),
  CommercialStage.SALE,
]);

export function getCommercialStageLabel(value: string | null | undefined): string {
  if (!value) {
    return '—';
  }

  if (value === CommercialStage.SALE || value === CommercialStage.CONVERTED) {
    return 'Venda';
  }

  return COMMERCIAL_STAGE_LABELS[value as CommercialStage] ?? value;
}

export function isCommercialStage(value: string): value is CommercialStage {
  return COMMERCIAL_STAGE_VALUES.has(value);
}

export function normalizeCommercialStage(value: string | null | undefined): string {
  if (!value) {
    return '';
  }

  return value === CommercialStage.SALE ? CommercialStage.CONVERTED : value;
}

/** Funnel position: Interesse = 0, 1º contato = 1, and so on. Unknown stages go last. */
export function getCommercialStageSortIndex(value: string | null | undefined): number {
  const stage = normalizeCommercialStage(value);
  const index = COMMERCIAL_STAGE_OPTIONS.findIndex((option) => option.value === stage);
  return index === -1 ? COMMERCIAL_STAGE_OPTIONS.length : index;
}

/** True when next is another known stage. The funnel can move forward or back. */
export function canAdvanceCommercialStage(
  current: string | null | undefined,
  next: string,
): boolean {
  const nextStage = normalizeCommercialStage(next);
  if (!nextStage || !isCommercialStage(nextStage)) {
    return false;
  }

  const currentStage = normalizeCommercialStage(current);
  if (!currentStage) {
    return true;
  }

  const currentIndex = COMMERCIAL_STAGE_OPTIONS.findIndex((option) => option.value === currentStage);
  const nextIndex = COMMERCIAL_STAGE_OPTIONS.findIndex((option) => option.value === nextStage);
  if (currentIndex < 0) {
    return true;
  }
  if (nextIndex < 0) {
    return false;
  }

  return nextIndex !== currentIndex;
}

export function canRescueDeclinedLead(current: string | null | undefined): boolean {
  return normalizeCommercialStage(current) === CommercialStage.DECLINED;
}

export function getDeclinedRescueStage(
  current: string | null | undefined,
): CommercialStage.FIRST_CONTACT | null {
  return canRescueDeclinedLead(current) ? CommercialStage.FIRST_CONTACT : null;
}

export function sortByCommercialStageOrder<T>(
  items: readonly T[],
  getStage: (item: T) => string,
): T[] {
  return [...items].sort((left, right) => (
    getCommercialStageSortIndex(getStage(left)) - getCommercialStageSortIndex(getStage(right))
  ));
}
